import React, { useState, useEffect } from 'react';
import { linkedInProfileApi, LinkedInProfile, CreateLinkedInProfileRequest } from '../services/api';
import { PageHeader, useConfirm, EmptyState } from '../components/ui';
import { FlowStepBar, useLinkedInFlow } from '../linkedin';
import { routeById } from '../navigation/routes';

const LinkedInProfilesPage: React.FC = () => {
  const { confirm } = useConfirm();
  const { refresh } = useLinkedInFlow();
  const [profiles, setProfiles] = useState<LinkedInProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateLinkedInProfileRequest>({
    name: '',
    username: '',
    profileUrl: '',
    notes: '',
    examplePosts: [],
  });
  const [newPost, setNewPost] = useState('');

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await linkedInProfileApi.getAll();
      setProfiles(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await linkedInProfileApi.update(editingId, formData);
      } else {
        await linkedInProfileApi.create(formData);
      }
      await loadProfiles();
      refresh();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (profile: LinkedInProfile) => {
    let examplePosts: string[] = [];
    try {
      if (profile.examplePosts) {
        examplePosts = JSON.parse(profile.examplePosts);
      }
    } catch {}

    setEditingId(profile.id);
    setFormData({
      name: profile.name,
      username: profile.username,
      profileUrl: profile.profileUrl || '',
      notes: profile.notes || '',
      examplePosts,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Profil löschen?',
      message: 'Das Referenzprofil und seine Beispiel-Posts werden entfernt.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await linkedInProfileApi.delete(id);
      await loadProfiles();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Loeschen');
    }
  };

  const addExamplePost = () => {
    if (!newPost.trim()) return;
    setFormData({
      ...formData,
      examplePosts: [...(formData.examplePosts || []), newPost.trim()],
    });
    setNewPost('');
  };

  const removeExamplePost = (index: number) => {
    const posts = [...(formData.examplePosts || [])];
    posts.splice(index, 1);
    setFormData({ ...formData, examplePosts: posts });
  };

  const resetForm = () => {
    setFormData({ name: '', username: '', profileUrl: '', notes: '', examplePosts: [] });
    setEditingId(null);
    setShowForm(false);
    setNewPost('');
  };

  const parseExamplePosts = (jsonStr: string | null | undefined): string[] => {
    if (!jsonStr) return [];
    try { return JSON.parse(jsonStr); } catch { return []; }
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div><span className="loading-text">Laden...</span></div>;
  }

  return (
    <div className="page-container">
      <FlowStepBar stepId="profiles" />
      <PageHeader
        title="LinkedIn-Referenzprofile"
        subtitle={routeById('li-profiles')?.description}
        actions={(
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
            {showForm ? 'Abbrechen' : '+ Neues Profil'}
          </button>
        )}
      />

      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Speichern Sie hier LinkedIn-Profile, die als Referenz und Inspiration dienen.
        Fuegen Sie Beispiel-Posts hinzu, um das System mit Best Practices zu fuettern.
        Diese Daten werden in Phase 1b fuer die Profil-Analyse verwendet.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="form-section" style={{ borderLeft: '4px solid #0077b5' }}>
          <h2 className="form-section-title">
            {editingId ? 'Profil bearbeiten' : 'Neues LinkedIn-Profil hinzufuegen'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="z.B. Max Mustermann"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">LinkedIn-Benutzername *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  placeholder="z.B. maxmustermann"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Profil-URL</label>
              <input
                type="text"
                value={formData.profileUrl || ''}
                onChange={(e) => setFormData({ ...formData, profileUrl: e.target.value })}
                placeholder="z.B. https://linkedin.com/in/maxmustermann"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Notizen (Warum ist dieses Profil eine Referenz?)</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="z.B. Sehr gutes Storytelling, hohe Engagement-Rate, ahnliche Zielgruppe..."
                rows={3}
                className="form-textarea"
              />
            </div>

            {/* Example Posts Section */}
            <div className="form-group">
              <label className="form-label">Beispiel-Posts ({(formData.examplePosts || []).length} gespeichert)</label>
              <p style={{ fontSize: '12px', color: 'var(--sec-fg-subtle)', margin: '0 0 8px 0' }}>
                Kopieren Sie erfolgreiche Posts von diesem Profil hier hinein. Diese dienen als Beispiele fuer die KI.
              </p>

              {(formData.examplePosts || []).map((post, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                  <pre style={{
                    flex: 1, whiteSpace: 'pre-wrap', fontSize: '13px',
                    backgroundColor: 'var(--sec-surface-alt)', padding: '8px 12px', borderRadius: '4px',
                    border: '1px solid var(--sec-border)', margin: 0, maxHeight: '100px', overflow: 'auto',
                  }}>
                    {post}
                  </pre>
                  <button
                    type="button"
                    onClick={() => removeExamplePost(idx)}
                    className="btn btn-danger btn-sm"
                    style={{ flexShrink: 0 }}
                  >
                    X
                  </button>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px' }}>
                <textarea
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  placeholder="Beispiel-Post hier einfuegen..."
                  rows={4}
                  className="form-textarea"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={addExamplePost}
                  className="btn btn-secondary"
                  style={{ flexShrink: 0, alignSelf: 'flex-end' }}
                  disabled={!newPost.trim()}
                >
                  + Hinzufuegen
                </button>
              </div>
            </div>

            <div className="btn-group">
              <button type="submit" className="btn btn-success">
                {editingId ? 'Aktualisieren' : 'Profil speichern'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Profiles List */}
      <div>
        <h2 className="form-section-title">Gespeicherte Profile ({profiles.length})</h2>
        {profiles.length === 0 ? (
          <EmptyState
            icon="users"
            title="Noch keine Referenzprofile"
            description="Sammle LinkedIn-Profile, deren Stil dir gefällt, und hinterlege ihre besten Posts. Die KI nutzt sie als Vorbild."
            action={{ label: 'Erstes Profil anlegen', onClick: () => setShowForm(true) }}
          />
        ) : (
          <div className="grid-cards-2">
            {profiles.map((profile) => {
              const posts = parseExamplePosts(profile.examplePosts);
              return (
                <div key={profile.id} className="card">
                  <div className="card-header">
                    <div>
                      <h3 className="card-title">{profile.name}</h3>
                      <p className="card-description" style={{ color: '#0077b5' }}>@{profile.username}</p>
                    </div>
                    <span style={{
                      padding: '2px 8px', backgroundColor: '#0077b5', color: 'white',
                      borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                    }}>
                      LinkedIn
                    </span>
                  </div>

                  {profile.profileUrl && (
                    <p className="text-sm" style={{ wordBreak: 'break-all' }}>
                      <strong>URL:</strong> {profile.profileUrl}
                    </p>
                  )}
                  {profile.notes && (
                    <p className="text-sm"><strong>Notizen:</strong> {profile.notes}</p>
                  )}
                  <p className="text-sm">
                    <strong>Beispiel-Posts:</strong> {posts.length}
                    {posts.length > 0 && (
                      <button
                        onClick={() => setExpandedId(expandedId === profile.id ? null : profile.id)}
                        style={{
                          background: 'none', border: 'none', color: 'var(--sec-coral-600)',
                          cursor: 'pointer', fontSize: '12px', marginLeft: '8px',
                        }}
                      >
                        {expandedId === profile.id ? 'Ausblenden' : 'Anzeigen'}
                      </button>
                    )}
                  </p>

                  {expandedId === profile.id && posts.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      {posts.map((post: string, idx: number) => (
                        <pre key={idx} style={{
                          whiteSpace: 'pre-wrap', fontSize: '12px', backgroundColor: 'var(--sec-surface-alt)',
                          padding: '8px', borderRadius: '4px', marginBottom: '8px',
                          border: '1px solid var(--sec-border)', maxHeight: '150px', overflow: 'auto',
                        }}>
                          {post}
                        </pre>
                      ))}
                    </div>
                  )}

                  <div className="card-footer">
                    <div className="btn-group">
                      <button onClick={() => handleEdit(profile)} className="btn btn-primary btn-sm">
                        Bearbeiten
                      </button>
                      <button onClick={() => handleDelete(profile.id)} className="btn btn-danger btn-sm">
                        Loeschen
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedInProfilesPage;
